import { useBookingContext } from '../BookingContext'
import { Card, CardContent } from '@w/components/ui/card'
import { Button } from '@w/components/ui/button'
import { format, parse } from 'date-fns'
import { cn } from '@w/lib/utils'
import { Clock, CalendarX2 } from 'lucide-react'

export default function TimeSlotPicker() {
  const { selectedDate, selectedTimeSlot, availability, selectTimeSlot } = useBookingContext()

  if (!selectedDate) {
    return (
      <Card className="border-none shadow-none">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-xl">Select a Time</h3>
          </div>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <CalendarX2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              Please choose a date from the calendar to see available time slots
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const slots = availability[selectedDate] || []

  // Format date for display
  const dateObj = parse(selectedDate, 'yyyy-MM-dd', new Date())
  const formattedDate = format(dateObj, 'EEEE, MMMM d')

  // Format time slot for display
  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString)
      return format(date, 'h:mm a')
    } catch (error) {
      return isoString
    }
  }

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-xl">Available Times</h3>
          </div>
          <p className="text-sm text-muted-foreground">{formattedDate}</p>
        </div>

        {slots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <CalendarX2 className="w-8 h-8 text-destructive" />
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              No available times for this date. Please select another date.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 max-h-[350px] overflow-y-auto pr-1">
            {slots.map((slot) => {
              const displayTime = formatTime(slot)
              const isSelected = selectedTimeSlot === slot

              return (
                <Button
                  key={slot}
                  variant={isSelected ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => selectTimeSlot(slot, displayTime)}
                  className={cn(
                    'justify-center transition-all duration-200 font-medium',
                    isSelected && 'ring-2 ring-primary ring-offset-2',
                    !isSelected && 'hover:border-primary/50 hover:bg-primary/5'
                  )}
                >
                  {displayTime}
                </Button>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
